// Iter 86.15 / Phase E7 Sprint 1 — Role Master + Permission UI (Phase B)
// Roles list at /admin/roles
//
// Layout:
//   - PageHeader with "+ New Role" CTA (super_admin only via role:write)
//   - Filter row: search by name/code + system|custom toggle + active|all toggle
//   - Table: code, name, type (system/custom badge), permissions count, users
//     holding, status, actions (View / Edit / Delete)
//
// Delete confirmation modal includes a payload showing "N users hold this
// role". If N > 0 the modal is informational (server-side blocks DELETE
// with 409 and lists holders).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MagnifyingGlass, Eye, PencilSimple, Trash, ShieldCheck, ChartBar, CopySimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "../../contexts/AuthContext";

export default function RolesList() {
    const { user } = useAuth();
    const canWrite = user?.role === "super_admin"; // server-side enforces; UI just gates the button
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filterType, setFilterType] = useState("all"); // all | system | custom
    const [filterActive, setFilterActive] = useState("active"); // active | all
    const [deleting, setDeleting] = useState(null);
    const [cloneSource, setCloneSource] = useState(null);
    const [cloneForm, setCloneForm] = useState({ code: "", name: "", description: "" });
    const [cloning, setCloning] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/role/list");
            setItems(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return items.filter((r) => {
            if (filterType === "system" && !r.is_system) return false;
            if (filterType === "custom" && r.is_system) return false;
            if (filterActive === "active" && !r.is_active) return false;
            if (s && !`${r.code} ${r.name} ${r.description || ""}`.toLowerCase().includes(s)) return false;
            return true;
        });
    }, [items, search, filterType, filterActive]);

    const doDelete = async (code) => {
        setDeleting(code);
        try {
            await api.delete(`/admin/role/${code}`);
            toast.success(`Role '${code}' deleted`);
            await reload();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (typeof detail === "object" && detail?.users_holding_role) {
                toast.error(`Cannot delete: ${detail.users_holding_role} user(s) still hold this role. Reassign them first.`);
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setDeleting(null);
        }
    };

    const openClone = (role) => {
        const suggested = `${role.code}_copy`;
        setCloneSource(role);
        setCloneForm({
            code: suggested.slice(0, 32),
            name: `${role.name} (copy)`,
            description: "",
        });
    };

    const submitClone = async () => {
        if (!cloneSource) return;
        const code = cloneForm.code.trim().toLowerCase();
        const name = cloneForm.name.trim();
        if (!code || !name) {
            toast.error("New role code and display name are required");
            return;
        }
        setCloning(true);
        try {
            const r = await api.post(`/admin/role/${cloneSource.code}/clone`, {
                code,
                name,
                description: cloneForm.description.trim() || undefined,
            });
            toast.success(`Cloned to '${r.data.code}' (${r.data.permissions.length} permissions copied)`);
            setCloneSource(null);
            await reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCloning(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="roles-list-page">
            <PageHeader
                title="Roles"
                subtitle="Application permission roles. Department / designation come from Employee Master, not from roles."
                breadcrumbs={[{ label: "Administration" }, { label: "Roles" }]}
                actions={
                    <div className="flex items-center gap-2">
                        {/* UAT RBAC-UX-04 (2026-07-14) — Permission Matrix is an
                            engineering diagnostic surface (raw permission codes).
                            Gate its entry point behind Primary Super Admin
                            ("Internal Engineering Mode") so normal admins are
                            not led into a code-first view. Route itself remains
                            reachable via direct URL for PSA bookmarks. */}
                        {user?.is_primary_admin === true && (
                            <Link to="/admin/roles/matrix">
                                <Button variant="outline" data-testid="open-permission-matrix-btn">
                                    <ChartBar className="w-4 h-4 mr-2" /> Permission Matrix
                                </Button>
                            </Link>
                        )}
                        <Link to="/admin/audit-log">
                            <Button variant="outline" data-testid="open-audit-log-btn">
                                <ShieldCheck className="w-4 h-4 mr-2" /> Audit Log
                            </Button>
                        </Link>
                        {canWrite && (
                            <Link to="/admin/roles/new">
                                <Button data-testid="new-role-btn">
                                    <Plus className="w-4 h-4 mr-2" /> New Role
                                </Button>
                            </Link>
                        )}
                    </div>
                }
            />

            <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3">
                <div className="relative">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search roles by name / code / description"
                        className="pl-9 w-80"
                        data-testid="roles-search-input"
                    />
                </div>
                <div className="flex items-center gap-1 text-xs">
                    <span className="text-slate-500 mr-1">Type:</span>
                    {["all", "system", "custom"].map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => setFilterType(t)}
                            data-testid={`filter-type-${t}`}
                            className={`px-3 py-1.5 rounded-md font-medium capitalize ${
                                filterType === t
                                    ? "bg-slate-900 text-white"
                                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                        >
                            {t}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-1 text-xs">
                    <span className="text-slate-500 mr-1">Status:</span>
                    {["active", "all"].map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => setFilterActive(t)}
                            data-testid={`filter-status-${t}`}
                            className={`px-3 py-1.5 rounded-md font-medium capitalize ${
                                filterActive === t
                                    ? "bg-slate-900 text-white"
                                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                        >
                            {t}
                        </button>
                    ))}
                </div>
                <div className="ml-auto text-xs text-slate-500" data-testid="roles-count">
                    {filtered.length} of {items.length} role{items.length === 1 ? "" : "s"}
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-slate-500" data-testid="roles-loading">Loading...</div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    title="No roles match"
                    description={search ? `No roles match "${search}".` : "Create your first custom role to get started."}
                />
            ) : (
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="roles-table">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-slate-600 text-xs uppercase tracking-wide">
                            <tr>
                                <th className="px-4 py-3">Role</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3 text-right">Permissions</th>
                                <th className="px-4 py-3 text-right">Users</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((r) => (
                                <tr key={r.code} className="hover:bg-slate-50" data-testid={`role-row-${r.code}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-slate-900">{r.name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{r.code}</div>
                                        {r.description && (
                                            <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{r.description}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {r.is_system ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-100 text-indigo-700">
                                                <ShieldCheck className="w-3 h-3 mr-1" /> System
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-100 text-emerald-700">
                                                Custom
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-xs">{r.permissions_count}</td>
                                    <td className="px-4 py-3 text-right font-mono text-xs">{r.users_holding_count}</td>
                                    <td className="px-4 py-3">
                                        <StatusBadge status={r.is_active ? "Active" : "Inactive"} />
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <Link to={`/admin/roles/${r.code}`}>
                                                <Button variant="ghost" size="sm" data-testid={`view-role-${r.code}`} title="View">
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                            </Link>
                                            {canWrite && r.code !== "super_admin" && (
                                                <Link to={`/admin/roles/${r.code}/edit`}>
                                                    <Button variant="ghost" size="sm" data-testid={`edit-role-${r.code}`} title="Edit">
                                                        <PencilSimple className="w-4 h-4" />
                                                    </Button>
                                                </Link>
                                            )}
                                            {canWrite && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openClone(r)}
                                                    className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                                    data-testid={`clone-role-${r.code}`}
                                                    title={`Clone '${r.name}' into a new custom role`}
                                                >
                                                    <CopySimple className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {canWrite && !r.is_system && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        if (confirm(`Delete role '${r.name}'?\nThis cannot be undone. If any active user holds this role, the server will reject the deletion.`)) {
                                                            doDelete(r.code);
                                                        }
                                                    }}
                                                    disabled={deleting === r.code}
                                                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                                    data-testid={`delete-role-${r.code}`}
                                                    title="Delete"
                                                >
                                                    <Trash className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Clone Role modal */}
            <Dialog open={!!cloneSource} onOpenChange={(o) => !o && setCloneSource(null)}>
                <DialogContent className="max-w-md" data-testid="clone-role-modal">
                    <DialogHeader>
                        <DialogTitle>Clone role</DialogTitle>
                        <DialogDescription>
                            Create a new custom role with the same permissions as{" "}
                            <span className="font-mono text-slate-800">{cloneSource?.code}</span>{" "}
                            ({cloneSource?.permissions_count} permission{cloneSource?.permissions_count === 1 ? "" : "s"}).
                            You can edit the permissions immediately after cloning.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="clone-code">
                                New role code <span className="text-rose-500">*</span>
                            </Label>
                            <Input
                                id="clone-code"
                                value={cloneForm.code}
                                onChange={(e) => setCloneForm((f) => ({ ...f, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))}
                                placeholder="senior_developer"
                                data-testid="clone-role-code-input"
                            />
                            <p className="text-xs text-slate-500">Lowercase, [a-z0-9_]. 2-32 chars.</p>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="clone-name">
                                Display name <span className="text-rose-500">*</span>
                            </Label>
                            <Input
                                id="clone-name"
                                value={cloneForm.name}
                                onChange={(e) => setCloneForm((f) => ({ ...f, name: e.target.value }))}
                                placeholder="Senior Developer"
                                data-testid="clone-role-name-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="clone-desc">Description</Label>
                            <Input
                                id="clone-desc"
                                value={cloneForm.description}
                                onChange={(e) => setCloneForm((f) => ({ ...f, description: e.target.value }))}
                                placeholder="(optional — auto-generated from source if blank)"
                                data-testid="clone-role-description-input"
                            />
                        </div>
                        <div className="bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 text-xs text-indigo-800">
                            The cloned role is created as a <strong>custom</strong> role (not system), even when cloning from a system role. Edit and delete are then allowed.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCloneSource(null)} disabled={cloning} data-testid="clone-role-cancel-btn">
                            Cancel
                        </Button>
                        <Button onClick={submitClone} disabled={cloning} data-testid="clone-role-submit-btn">
                            <CopySimple className="w-4 h-4 mr-2" />
                            {cloning ? "Cloning..." : "Clone role"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
